import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '../common/Toast';
import {
  getWorkItemReferences,
  searchWorkItemsForReference,
  addWorkItemReference,
  removeWorkItemReference,
} from '../../api/workItemReferences';

/**
 * 工作項目的關聯清單(手動設定)。
 * 取代原本從內文 #編號 自動解析關聯的做法 —— 打 # 只是想提一下編號,不代表要建立關聯,
 * 兩件事混在一起會長出使用者沒有要的關聯,所以改成在這裡明確地加與刪。
 * 關聯是雙向的:A 關聯 B 之後,B 的清單也會出現 A。
 */
const WorkItemReferences = ({ item, companyId, userId, statusMeta, onOpenItem }) => {
  const toast = useToast();
  const [refs, setRefs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  const itemId = item?.id;

  const load = useCallback(async () => {
    if (!itemId) return;
    const { data } = await getWorkItemReferences(itemId);
    setRefs(data || []);
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  // 收合搜尋框時把狀態一起清掉,下次打開不會殘留上次的字與結果
  const closeAdd = useCallback(() => {
    setAdding(false);
    setQuery('');
    setResults([]);
  }, []);

  useEffect(() => {
    if (!adding) return undefined;
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) closeAdd(); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [adding, closeAdd]);

  useEffect(() => {
    if (!adding) return undefined;
    const term = query.trim();
    if (!term) { setResults([]); setSearching(false); return undefined; }
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const excludeIds = [itemId, ...refs.map((r) => r.item.id)];
      const found = await searchWorkItemsForReference(term, companyId, { excludeIds });
      setResults(found);
      setSearching(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, adding, companyId, itemId, refs]);

  const handleAdd = async (target) => {
    if (busy) return;
    setBusy(true);
    const { error } = await addWorkItemReference(item, target, { companyId, userId });
    setBusy(false);
    if (error) { toast.error('新增關聯失敗'); return; }
    closeAdd();
    load();
  };

  const handleRemove = async (ref) => {
    if (busy) return;
    setBusy(true);
    const { error } = await removeWorkItemReference(ref.refId, { sourceItem: item, targetItem: ref.item, companyId });
    setBusy(false);
    if (error) { toast.error('移除關聯失敗'); return; }
    load();
  };

  const itemNo = (i) => (i?.work_item_no != null ? `#${i.work_item_no}` : null);

  return (
    <div className="wd-sb-field wd-refs" ref={boxRef}>
      <span className="wd-sb-label">關聯工作項目</span>

      {refs.length > 0 && (
        <div className="wd-ref-list">
          {refs.map((ref) => {
            const sm = statusMeta(ref.item.status);
            return (
              <div className="wd-ref-row" key={ref.refId}>
                <button
                  type="button"
                  className="wd-ref-open"
                  onClick={() => onOpenItem?.(ref.item.id)}
                  title={ref.item.title}
                >
                  <span className="wd-ref-dot" style={{ background: sm.color }} />
                  {itemNo(ref.item) && <span className="wd-ref-no">{itemNo(ref.item)}</span>}
                  <span className="wd-ref-title">{ref.item.title}</span>
                </button>
                <button type="button" className="wd-remove-x" onClick={() => handleRemove(ref)} title="移除關聯">×</button>
              </div>
            );
          })}
        </div>
      )}

      {adding ? (
        <div className="wd-ref-search">
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); closeAdd(); }
              if (e.key === 'Enter' && results.length > 0) { e.preventDefault(); handleAdd(results[0]); }
            }}
            placeholder="搜尋標題或說明…（#編號 搜尋指定項目）"
          />
          {query.trim() !== '' && (
            <div className="wd-ref-results">
              {searching && <div className="wd-ref-hint">搜尋中…</div>}
              {!searching && results.length === 0 && <div className="wd-ref-hint">找不到符合的工作項目</div>}
              {!searching && results.map((r) => {
                const sm = statusMeta(r.status);
                return (
                  <button type="button" className="wd-ref-result" key={r.id} onClick={() => handleAdd(r)}>
                    <span className="wd-ref-dot" style={{ background: sm.color }} />
                    {itemNo(r) && <span className="wd-ref-no">{itemNo(r)}</span>}
                    <span className="wd-ref-title">{r.title}</span>
                    {r.projects?.name && <span className="wd-ref-proj">{r.projects.name}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="wd-add-collab" onClick={() => setAdding(true)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 2v10M2 7h10" strokeLinecap="round" /></svg>
          新增關聯
        </button>
      )}
    </div>
  );
};

export default WorkItemReferences;
